import React, { useEffect, useMemo, useState } from 'react';
import { SeoIcon, ContentIcon, PaidMediaIcon } from 'assets/icons/Modules-Icon';
import { useTranslation } from 'react-i18next';
import { ExternalLinkIcon, ReadIcon, UnreadIcon } from 'assets/icons/Action-Icon';
import { listenToNotifications, updateNotificationRead } from 'utils/notifyActivities';
import { useAuth, changeAccount } from 'hooks/useAuth';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { useNavigate } from 'react-router-dom';
import CustomPagination from 'pages/keywords/components/Pagination';
import { PATHS, TYPE_LABELS, TYPE_STYLES } from 'constants/myActivities';
import {
  TableWrapper,
  Table,
  HeaderCell,
  ActivityCell,
  AccountCell,
  Row,
  Title,
  TypeTag,
  ActionButton,
  Checkbox,
} from './styles';

const areaIcons = {
  seo: <SeoIcon />,
  content: <ContentIcon />,
  'paid-media': <PaidMediaIcon />,
};

const UserTasksTable = () => {
  const { t: translate } = useTranslation();
  const { user, changeAccount } = useAuth();
  const accountId = user?.account;
  const [activities, setActivities] = useState([]);
  const [parsedActivities, setParsedActivities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [linesByPage] = useState(10);

  const unreadCount = useMemo(
    () => parsedActivities.filter((a) => !a.read).length,
    [parsedActivities],
  );
  const paginatedActivities = useMemo(() => {
    const start = (currentPage - 1) * linesByPage;
    const end = start + linesByPage;

    return parsedActivities.slice(start, end);
  }, [parsedActivities, currentPage, linesByPage]);

  const totalPages = Math.ceil(parsedActivities.length / linesByPage);
  const showBulkActions = selected.length > 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const toggleSelectAll = (checked) => {
    setSelected(checked ? parsedActivities.map((a) => a.id) : []);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleRead = (id) => {
    setActivities((prev) =>
      prev.map((activity) => {
        if (activity.id !== id) return activity;

        updateNotificationRead(user.id, activity.id, !activity.read);

        return {
          ...activity,
          read: !activity.read,
        };
      }),
    );
  };

  const toggleReadSelected = (read) => {
    setActivities((prev) =>
      prev.map((activity) => {
        if (!selected.includes(activity.id)) return activity;

        updateNotificationRead(user.id, activity.id, !activity.read);

        return {
          ...activity,
          read,
        };
      }),
    );
  };

  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = listenToNotifications(user.id, (notifications) => {
      if (!notifications) return;

      setActivities(
        notifications.map((n) => ({
          id: n.id,
          type: n.type,
          read: n.read ?? false,
          columnId: n.columnId,
          itemId: n.itemId,
          area: n.area,
          accountId: n.accountId,
          createdAt: n.createdAt,
        })),
      );
    });

    return () => unsubscribe();
  }, [accountId, user]);

  useEffect(() => {
    if (!activities.length) return;

    const fetchParsedActivities = async () => {
      try {
        const firebase = getFirebaseBackend();
        const activitiesPromises = activities.map(async (activity) => {
          const task = await firebase.getItemName(
            activity.area,
            activity.accountId,
            activity.columnId,
            activity.itemId,
          );
          const accountName = await firebase.accountName(activity.accountId);

          return {
            ...activity,
            title: task || 'Sem título',
            accountName,
          };
        });

        const newParsedActivities = await Promise.all(activitiesPromises);

        setParsedActivities(newParsedActivities);
      } catch (error) {
        console.error(error);
        setParsedActivities([]);
      }
    };

    fetchParsedActivities();
  }, [activities, accountId]);

  const openActivity = async (activity) => {
    const modulePath = PATHS[activity.area];

    if (!activity?.itemId || !modulePath) {
      console.error('Dados inválidos');
      return;
    }

    try {
      if (activity.accountId !== user?.account) {
        await changeAccount(user.id, activity.accountId);

        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const path = `/${modulePath}?id=${activity.itemId}&acc=${activity.accountId}&col=${activity.columnId}`;

      navigate(path);
    } catch (error) {
      console.error('Erro ao abrir atividade:', error);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const maxPage = Math.ceil(activities.length / linesByPage) || 1;
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [activities, linesByPage, currentPage]);

  return (
    <TableWrapper>
      <Table
        style={{
          borderRadius: 8,
          outline: '1px #F6F6F6 solid',
          outlineOffset: '-1px',
          height: '48px',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                borderRight: '1px #EAEAEA solid',
                borderRadius: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <HeaderCell style={{ alignItems: 'normal' }}>
                <Checkbox
                  type="checkbox"
                  checked={showBulkActions}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
                <span>
                  {unreadCount}{' '}
                  {unreadCount === 1
                    ? translate('Activity').toLowerCase()
                    : translate('Activities').toLowerCase()}
                </span>
                {showBulkActions && (
                  <div
                    style={{
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <ActionButton
                      style={{ height: 28, width: 28 }}
                      onClick={() => toggleReadSelected(true)}
                    >
                      <ReadIcon />
                    </ActionButton>
                    <ActionButton
                      style={{ height: 28, width: 28 }}
                      onClick={() => toggleReadSelected(false)}
                    >
                      <UnreadIcon />
                    </ActionButton>
                  </div>
                )}
              </HeaderCell>
            </th>
            <th style={{ width: '100px', borderRight: '1px #EAEAEA solid' }}>
              {translate('Area')}
            </th>
            <th style={{ width: '180px', borderRight: '1px #EAEAEA solid' }}>
              {translate('Account')}
            </th>
            <th style={{ width: '180px', borderRight: '1px #EAEAEA solid' }}>
              {translate('Type')}
            </th>
            <th style={{ width: '10px' }}>{translate('Action')}</th>
          </tr>
        </thead>

        <tbody>
          {paginatedActivities.map((activity) => (
            <Row key={activity.id} read={activity.read}>
              <td>
                <ActivityCell>
                  <Checkbox
                    type="checkbox"
                    checked={selected.includes(activity.id)}
                    onChange={() => toggleSelect(activity.id)}
                  />
                  <Title read={activity.read}>{activity.title}</Title>
                </ActivityCell>
              </td>
              <td>{areaIcons[activity.area]}</td>

              <td>
                <AccountCell>
                  <span>{activity.accountName}</span>
                </AccountCell>
              </td>

              <td>
                <TypeTag color={TYPE_STYLES[activity.type]}>
                  {translate(TYPE_LABELS[activity.type])}
                </TypeTag>
              </td>

              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <ActionButton onClick={() => openActivity(activity)}>
                    <ExternalLinkIcon />
                  </ActionButton>
                  <ActionButton onClick={() => toggleRead(activity.id)}>
                    {activity.read ? <ReadIcon /> : <UnreadIcon />}
                  </ActionButton>
                </div>
              </td>
            </Row>
          ))}
        </tbody>
      </Table>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <CustomPagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          linesByPage={linesByPage}
        />
      </div>
    </TableWrapper>
  );
};

export default UserTasksTable;
